import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { EventSessionTypePolicyDto } from '@/types/dtos/media/media-settings.dto';
import { GET_EVENT_SETTINGS_QUERY_KEY } from '@/state/queries/events/use-get-event-settings';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';

export const usePatchEventSessionTypesSettingsMutation = (options?: { onSuccess?: () => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      byType,
    }: {
      eventId: string;
      byType: Record<string, Partial<EventSessionTypePolicyDto>>;
    }) => {
      const token = await getToken();
      return patchEventSessionTypes(eventId, byType, token ?? '');
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SETTINGS_QUERY_KEY, variables.eventId] }),
      ]);
      options?.onSuccess?.();
    },
  });
};

const patchEventSessionTypes = async (
  eventId: string,
  byType: Record<string, Partial<EventSessionTypePolicyDto>>,
  token: string,
) => {
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/settings/session-types`;
  const sanitizedByType = Object.fromEntries(
    Object.entries(byType).filter(([, policy]) => policy !== undefined && Object.keys(policy).length > 0),
  );
  return sendJSONRequest({ url, method: METHOD.PATCH, body: { sessionTypes: { byType: sanitizedByType } }, token });
};
